import { captureMessage } from "@sentry/nextjs";
import { USER_TRACKING_EVENTS } from "@website/tracking/tracking.types";
import type { Core } from "@website/tracking/types/core";
import { addEventToTracking } from "@website/tracking/user";
import { Metric } from "@website/widget/scripts/web-vitals/types";
import type { WebVitalDiagnosticsPayload } from "@website/widget/scripts/web-vitals/utils";
import { getDetailedDiagnostics } from "@website/widget/scripts/web-vitals/utils";

type CoreWebVitalMetric = NonNullable<Core["core_web_vitals"]>;
const LARGE_INP_NUMBER = 100_000_000;

function reportLargeInp(
  metric: Metric,
  trackingPayload: CoreWebVitalMetric
): void {
  if (metric.name !== "INP") return;

  const entries = (metric.entries || []).map((entry: PerformanceEntry) => {
    const event = entry as PerformanceEventTiming & { interactionId?: number };

    return {
      duration: event.duration,
      inputDelay: event.processingStart - event.startTime,
      interactionId: event.interactionId,
      processingEnd: event.processingEnd,
      processingStart: event.processingStart,
      processingTime: event.processingEnd - event.processingStart,
      startTime: event.startTime
    };
  });
  const hasLargeNumber = [metric.value, ...entries.flatMap(Object.values)].some(
    (value) => typeof value === "number" && Math.abs(value) >= LARGE_INP_NUMBER
  );

  if (!hasLargeNumber) return;

  captureMessage("large_inp_comparison", {
    level: "warning",
    tags: { metric_id: metric.id },
    extra: {
      nextWebVitals: {
        delta: metric.delta,
        entries,
        value: metric.value
      },
      trackingPayload: {
        delta: trackingPayload.delta,
        diagnostics: {
          duration: trackingPayload.diagnostics?.duration,
          inputDelay: trackingPayload.diagnostics?.input_delay,
          interactionId: trackingPayload.diagnostics?.interaction_id,
          processingEnd: trackingPayload.diagnostics?.processing_end,
          processingStart: trackingPayload.diagnostics?.processing_start,
          processingTime: trackingPayload.diagnostics?.processing_time,
          startTime: trackingPayload.diagnostics?.start_time
        },
        value: trackingPayload.value
      }
    }
  });
}

export function reportWebVitalMetric(metric: Metric): void {
  if (metric.name === "FID" || metric.rating === "good") return;

  const diagnostics: WebVitalDiagnosticsPayload =
    getDetailedDiagnostics(metric);

  const coreWebVitals: CoreWebVitalMetric = {
    name: metric.name,
    value: metric.value,
    rating: metric.rating,
    delta: metric.delta,
    id: metric.id,
    navigation_type: metric.navigationType,
    label: "",
    attribution: {},
    diagnostics: diagnostics as CoreWebVitalMetric["diagnostics"]
  };

  reportLargeInp(metric, coreWebVitals);

  addEventToTracking(USER_TRACKING_EVENTS.WEB_VITAL_MEASURED, {
    product: "core",
    core_web_vitals: coreWebVitals
  });
}
